import { useEffect, useState } from 'react';
import { ApiError, artApi, type ArtChoice, type ArtOptions } from '../api';
import { clearArtCaches } from '../meta';
import { clearWidgetCache } from './Widgets';

type Ref = { type: 'movie' | 'tv'; tmdbId: number } | null;
type Kind = 'poster' | 'backdrop' | 'logo';

// Mag dit account beheren? Eén keer per pagina vragen
let canManage: Promise<boolean> | null = null;
function useCanManage(): boolean {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    canManage ??= artApi.config().then((c) => c.canManage).catch(() => false);
    void canManage.then(setOk);
  }, []);
  return ok;
}

/** Knop "Artwork kiezen" op de titelpagina; alleen voor de beheerder. */
export function ArtPickerButton({ refItem, title }: { refItem: Ref; title: string }) {
  const can = useCanManage();
  const [open, setOpen] = useState(false);
  if (!can || !refItem) return null;
  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm art-pick-btn" onClick={() => setOpen(true)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>
        Artwork kiezen
      </button>
      {open && <ArtPicker refItem={refItem} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}

const TABS: { id: Kind; label: string }[] = [
  { id: 'poster', label: 'Posters' },
  { id: 'backdrop', label: 'Achtergronden' },
  { id: 'logo', label: "Logo's" },
];

function ArtPicker({ refItem, title, onClose }: { refItem: NonNullable<Ref>; title: string; onClose: () => void }) {
  const [opts, setOpts] = useState<ArtOptions | null>(null);
  const [tab, setTab] = useState<Kind>('poster');
  // Gekozen maar nog niet opgeslagen; null = automatisch
  const [pick, setPick] = useState<Partial<Record<Kind, string | null>>>({});
  const [plex, setPlex] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    artApi.options(refItem.type, refItem.tmdbId).then(setOpts).catch(() => setMsg('Artwork laden mislukt.'));
  }, [refItem.type, refItem.tmdbId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const list: ArtChoice[] = !opts ? [] : tab === 'poster' ? opts.posters : tab === 'backdrop' ? opts.backdrops : opts.logos;
  const saved = opts?.override?.[tab] ?? null;
  const current = pick[tab] !== undefined ? pick[tab] : saved;
  const auto = opts?.auto?.[tab] ?? null;
  const dirty = Object.keys(pick).length > 0;

  const save = async (reset = false) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await artApi.override(refItem.type, refItem.tmdbId, reset ? { reset: true } : { ...pick, plex: plex && !!opts?.inPlex });
      setOpts((o) => (o ? { ...o, override: r.override } : o));
      setPick({});
      clearArtCaches();
      clearWidgetCache();
      setMsg(
        reset ? 'Alles weer automatisch.'
          : r.plex === 'done' ? 'Opgeslagen, ook in Plex.'
          : r.plex === 'failed' ? 'Opgeslagen in tumedia; in Plex zetten lukte niet.'
          : 'Opgeslagen.',
      );
    } catch (e) {
      setMsg(e instanceof ApiError && e.message !== e.code ? e.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };

  const thumb = (url: string, preview: string, label: string, meta?: string) => {
    const on = current === url || (current === null && auto === url);
    return (
      <button key={url} type="button" className={`art-thumb art-thumb-${tab} ${on ? 'is-on' : ''}`} aria-pressed={on} aria-label={label} onClick={() => setPick((p) => ({ ...p, [tab]: url }))}>
        <img src={preview} alt="" loading="lazy" referrerPolicy="no-referrer" />
        {meta && <span className="art-thumb-meta">{meta}</span>}
        {current === url && <span className="art-thumb-pin">Vastgezet</span>}
      </button>
    );
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal art-modal" role="dialog" aria-modal="true" aria-label={`Artwork kiezen voor ${title}`} onClick={(e) => e.stopPropagation()}>
        <div className="art-modal-head">
          <h2>Artwork · {title}</h2>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Sluiten</button>
        </div>
        <div className="filterbar" role="tablist" aria-label="Soort" data-focus-group style={{ margin: 0 }}>
          {TABS.map((t) => (
            <button key={t.id} role="tab" type="button" className="chip" aria-selected={tab === t.id} aria-pressed={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}{pick[t.id] !== undefined ? ' •' : ''}
            </button>
          ))}
        </div>
        <p className="subtle hint">
          {current ? 'Vastgezet voor iedereen.' : 'Automatisch (taalvoorkeur, dan meeste likes).'} Klik op een afbeelding om die te kiezen{tab === 'logo' ? '' : ', of kies Automatisch'}.
        </p>
        {!opts && !msg && <p className="placeholder-row">Artwork laden…</p>}
        <div className={`art-grid art-grid-${tab}`} data-focus-group>
          <button type="button" className={`art-thumb art-thumb-${tab} art-auto ${current === null ? 'is-on' : ''}`} aria-pressed={current === null} onClick={() => setPick((p) => ({ ...p, [tab]: null }))}>
            {auto ? <img src={auto} alt="" loading="lazy" referrerPolicy="no-referrer" /> : null}
            <span className="art-thumb-meta">Automatisch</span>
          </button>
          {tab === 'poster' && opts?.spatial && thumb(opts.spatial, opts.spatial, 'SpatialPosters', 'SpatialPosters')}
          {list.map((c) => thumb(c.url, c.preview, `${c.source === 'fanart' ? 'Fanart.tv' : 'TMDB'}${c.lang ? `, ${c.lang}` : ', zonder tekst'}`, `${c.source === 'fanart' ? 'Fanart' : 'TMDB'} · ${c.lang ? c.lang.toUpperCase() : 'zonder tekst'} · ${c.source === 'fanart' ? `♥ ${c.likes}` : `★ ${c.likes}`}`))}
          {opts && list.length === 0 && <p className="subtle">Geen {TABS.find((t) => t.id === tab)!.label.toLowerCase()} gevonden.</p>}
        </div>
        <div className="art-modal-foot">
          {opts?.inPlex && (
            <label className="check-row">
              <input type="checkbox" checked={plex} onChange={(e) => setPlex(e.target.checked)} />
              Ook in Plex zetten (poster en achtergrond; dan zien Plex en de apps het ook)
            </label>
          )}
          {msg && <span className="subtle" role="status">{msg}</span>}
          <div className="art-modal-actions">
            {opts?.override && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void save(true)}>Alles automatisch</button>}
            <button type="button" className="btn btn-primary" disabled={busy || !dirty} onClick={() => void save()}>{busy ? 'Opslaan…' : 'Opslaan'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
